// tslint:disable:object-literal-sort-keys

/**
 * @file
 * BasstrakkMap
 *
 * Interactive map that shows angler locations and/or catches based on layer
 * choice (catches or locations) and filters (Top 12, All, or specified angler).
 */
import * as React from 'react';

import { meta } from './meta';

// Utils
import * as api from '../../util/api';
import { refreshRate } from '../../util/util';
import {
  getAnglerById,
  getAnglerOptionTestId,
  getDataById,
  getTop12Anglers,
  getTop12Catches,
  processCatchesData,
  processLocationData,
} from './processing';

// Components
import BassComponent from '../../components/BassComponent/BassComponent';
import BasstrakkMapDisplay from './components/BasstrakkMapDisplay';
import BasstrakkMapNavFilter from './components/BasstrakkMapNavFilter';
import BasstrakkMapSidebar from './components/BasstrakkMapSidebar';

// Styles
import 'leaflet/dist/leaflet.css';
// @Bass-Blocks-Integration: Import below commented out for build, leaving in case missing styles are actually needed/used.
// import './basstrakk-map.scss';

// Types
import {
  AnglerId,
  IBasstrakkAngler,
  IBasstrakkCatch,
  RefreshRate,
  TournamentId,
} from '../../types';
import {
  FilterLabel,
  FilterType,
  IBaseData,
  ICatch,
  ILocation,
  IMarker,
  LayerType,
} from './types';

// Constants
// 5 min refresh rate in ms.
const COMPONENT_REFRESH_RATE = refreshRate([300000, 300000]);

interface IProps {
  // Initial lat for map from Drupal settings.
  lat: number;
  // Initial lng from Drupal settings.
  lng: number;
  // Tournament Id
  tid: TournamentId;
  // Optional refresh rate, defaults to 5 mins.
  refreshRate?: RefreshRate;
}

interface IState {
  // Current active filter (all, top12, angler)
  activeFilter: FilterType;
  // Current active layer (locations or catches)
  activeLayer: LayerType;
  // Angler catches data.
  catchesData: ICatch[];
  // Angler location data.
  locationData: ILocation[];
  // Map markers for locations and catches.
  markers: IMarker[];
  // Angler Id of selected angler in filter.
  selectedAngler: AnglerId;
  // Aggregate data for sidebar display.
  sidebarData: IMarker | {};
  // Default map zoom setting.
  zoom: number;
}

export const MarkerContext = React.createContext({
  locationData: [] as ILocation[],
  // @ts-ignore
  onMarkerClick: (sidebarData: IMarker): void => undefined,
});

export class BasstrakkMap extends React.PureComponent<IProps, IState> {
  public static defaultProps = {
    // Set lat/lng default values.
    lat: 35.91839,
    lng: -96.50017,
    refreshRate: COMPONENT_REFRESH_RATE,
  };

  public state: IState = {
    activeFilter: FilterType.ALL,
    activeLayer: LayerType.LOCATIONS,
    catchesData: [],
    locationData: [],
    markers: [],
    selectedAngler: 0,
    sidebarData: {},
    zoom: 8,
  };

  // API endpoint to bring in data on Angler catches.
  private catchesUri = `/wp-json/data/v1/tournament/basstrakk/catches?tournament_id=${this.props.tid}&daydate=${window.bassConfig?.bass_leaderboard?.daydate}`;

  /**
   * Sets sidebarData state for catches or angler locations clicked.
   */
  public handleMarkerClick = (sidebarData: IMarker) =>
    this.setState(state =>
      state.activeLayer === LayerType.CATCHES
        ? // If we're looking at catches, prepare the sidebar data
          {
            sidebarData: {
              ...(state.locationData.find(
                ({ anglerId }) => anglerId === sidebarData.anglerId
              ) || {}),
              ...sidebarData,
              anglerName: getAnglerById(
                sidebarData.anglerId,
                state.locationData
              ).anglerName,
              showCatchesDiv: true,
            },
          }
        : // Otherwise, just pass right through
          { sidebarData }
    );

  /**
   * Handle data filter when user clicks a filter in the top nav.
   * Passed down to BasstrakkMapNavFilter component to lift state based on user
   * selection.
   */
  public handleFilterClick = (activeFilter: FilterType) =>
    this.setState({ activeFilter }, () => this.displayMarkers());

  /**
   * Set the angler selected from dropdown to state.
   */
  public handleAnglerSelect = (angler: string) =>
    this.setState({ selectedAngler: parseInt(angler, 10) });

  /**
   * Update layer to location or catches based on layer filter clicked.
   */
  public handleLayerClick = (layer: LayerType) => (
    // @ts-ignore
    e: React.MouseEvent<HTMLButtonElement>
  ) =>
    this.setState(
      {
        activeLayer:
          layer === LayerType.LOCATIONS
            ? LayerType.LOCATIONS
            : LayerType.CATCHES,
      },
      () => this.displayMarkers()
    );

  /**
   * Display map markers based on layer, filter, and data.
   */
  public displayMarkers = () => {
    const {
      activeLayer,
      activeFilter,
      catchesData,
      locationData,
      selectedAngler,
    } = this.state;
    let markers: IBaseData[] = [];

    // Display catches or location markers.
    switch (activeLayer) {
      case LayerType.CATCHES:
        markers = catchesData;
        break;
      case LayerType.LOCATIONS:
        markers = locationData;
        break;
    }

    // Filter data by all, top 12, or angler based on user selection.
    switch (activeFilter) {
      case FilterType.ALL:
        markers = this.catchesOrLocations();
        break;
      case FilterType.TOP_12:
        markers =
          activeLayer === LayerType.LOCATIONS
            ? getTop12Anglers(locationData)
            : getTop12Catches(catchesData);
        break;
      case FilterType.ANGLER:
        markers = getDataById(selectedAngler, this.catchesOrLocations());
        break;
    }

    this.setState({ markers });
  };

  /**
   * Return catches or locations data based on active layer.
   */
  public catchesOrLocations = () => {
    const { activeLayer, locationData, catchesData } = this.state;

    return activeLayer === LayerType.LOCATIONS ? locationData : catchesData;
  };

  /**
   * Callback passed to api library. Sets locationData state if data exists.
   */
  public fetchLocationData = () => (data: IBasstrakkAngler[]) => {
    if (data && data.length) {
      this.locationsStateSetter(data);
    }
  };

  /**
   * Callback passed to api library. Sets catchesData state if data exists.
   */
  public fetchCatchesData = () => (data: IBasstrakkCatch[]) => {
    if (data && data.length) {
      this.catchesStateSetter(data);
    }
  };

  /**
   * Set map latitude and longitude, set locationData state, update markers.
   */
  public locationsStateSetter = (
    basstrakkAnglers: IBasstrakkAngler[]
  ): void => {
    const { lat, lng } = this.props;
    // Process and format location data.
    const locationData = processLocationData(basstrakkAnglers, lat, lng);

    this.setState({ locationData }, () => this.displayMarkers());
  };

  /**
   * Set catchesData state and update markers.
   */
  public catchesStateSetter = (basstrakkCatches: IBasstrakkCatch[]): void => {
    const { locationData } = this.state;
    const { lat, lng } = this.props;

    if (locationData.length) {
      // Process and format catches data.
      const catchesData = processCatchesData(
        basstrakkCatches,
        locationData,
        lat,
        lng
      );

      this.setState({ catchesData }, () => this.displayMarkers());
    }
  };

  /**
   * Build options for angler select filter.
   */
  public buildAnglerSelect = () =>
    this.state.locationData.map(({ anglerName, anglerId }: ILocation) => (
      <option
        key={anglerId}
        value={anglerId}
        data-testid={getAnglerOptionTestId(anglerName, anglerId)}
      >
        {anglerName}
      </option>
    ));

  /**
   * Determine which filter radio button is checked based on activeFilter.
   */
  public isChecked = (filter: FilterType) => this.state.activeFilter === filter;

  public async componentDidMount() {
    const {
      bass_leaderboard: {
        daydate = '',
        new_url: newBaseUrl = '',
      } = {},
    } = bassConfig;
    const locationsUri = `${newBaseUrl}/tournament/basstrakk/leaderboard?tournament_id=${this.props.tid}&daydate=${daydate}`;

    // Fetch data and poll for location data using api library.
    await api.subscribe({
      callback: this.fetchLocationData(),
      uri: locationsUri,
      rate: COMPONENT_REFRESH_RATE,
    });

    // Fetch data and poll for catches data using api library.
    await api.subscribe({
      callback: this.fetchCatchesData(),
      uri: this.catchesUri,
      rate: COMPONENT_REFRESH_RATE,
    });
  }

  public render() {
    const {
      activeLayer,
      locationData,
      markers,
      sidebarData,
      zoom,
    } = this.state;
    const { lat, lng } = this.props;
    const optionItems = this.buildAnglerSelect();

    return (
      <div>
        <BassComponent meta={meta}>
          <div data-testid="basstrakk-map" id="basstrakk-map__component">
            {/* Top nav containing filters for the map. */}
            <nav className="basstrakk-map__top-nav">
              <div className="filter-buttons">
                <h3 className="filter-buttons__filter-title">
                  <span>Angler Display: </span>
                </h3>
                <BasstrakkMapNavFilter
                  label={FilterLabel.ALL}
                  isChecked={this.isChecked(FilterType.ALL)}
                  handleFilterClick={this.handleFilterClick}
                  filter={FilterType.ALL}
                />
                <BasstrakkMapNavFilter
                  label={FilterLabel.TOP_12}
                  isChecked={this.isChecked(FilterType.TOP_12)}
                  handleFilterClick={this.handleFilterClick}
                  filter={FilterType.TOP_12}
                />
                <BasstrakkMapNavFilter
                  label={FilterLabel.ANGLER}
                  isChecked={this.isChecked(FilterType.ANGLER)}
                  handleFilterClick={this.handleFilterClick}
                  filter={FilterType.ANGLER}
                  hasOptionSelect={true}
                  options={optionItems}
                  handleAnglerSelect={this.handleAnglerSelect}
                />
              </div>

              {/* Layer buttons: Locations / Catches */}
              <div className="basstrakk-map__layer-buttons">
                <label
                  className={`basstrakk-map__button-label js-basstrakk-map__showCatch ${
                    activeLayer === LayerType.CATCHES && 'button-label--active'
                  }`}
                  data-testid="catches-filter-label"
                >
                  <button
                    className="basstrakk-map__button-label__button-link"
                    onClick={this.handleLayerClick(LayerType.CATCHES)}
                    data-testid="catches-filter"
                  >
                    Catches
                  </button>
                </label>
                <label
                  className={`basstrakk-map__button-label js-basstrakk-map__showCurrentPosition ${
                    activeLayer === LayerType.LOCATIONS &&
                    'button-label--active'
                  }`}
                  data-testid="location-filter-label"
                >
                  <button
                    className="basstrakk-map__button-label__button-link"
                    onClick={this.handleLayerClick(LayerType.LOCATIONS)}
                    data-testid="location-filter"
                  >
                    Locations
                  </button>
                </label>
              </div>
            </nav>

            {/* Lower portion of the map that contains sidebar and leaflet map. */}
            <div
              id="basstrakk-map__details"
              className="basstrakk-map__container"
            >
              {/* Sidebar used to display data when markers are clicked. */}
              <BasstrakkMapSidebar
                defaultLat={lat}
                defaultLng={lng}
                sidebarData={sidebarData as IMarker}
              />
              <MarkerContext.Provider
                value={{
                  locationData,
                  onMarkerClick: this.handleMarkerClick,
                }}
              >
                {/* Leaflet map and markers. */}
                <BasstrakkMapDisplay
                  lat={lat}
                  lng={lng}
                  layer={activeLayer}
                  markers={markers}
                  zoom={zoom}
                />
              </MarkerContext.Provider>
            </div>
          </div>
        </BassComponent>
      </div>
    );
  }
}

export default BasstrakkMap;
